<template>
  <div class="flex justify-between">
    <div class="flex gap-12">
      <RevButton
        v-for="filter in quickFiltersValues"
        :key="filter.label"
        size="small"
        variant="secondary"
        @click="handleSeeAllWithinBackboxPriceRange(filter)"
      >
        {{ filter.label }}
      </RevButton>
    </div>
    <RevButton
      v-if="isDealIterationEnabled"
      :icon="IconDealFilled"
      size="small"
      variant="secondary"
      @click="handleSeeAllDealOpportunities"
    >
      {{ i18n(translations.dealOpportunitiesButton) }}
    </RevButton>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevButton } from '@ds/components/Button'
import { IconDealFilled } from '@ds/icons/IconDealFilled'

import type { GetListingsFiltersMetadataResponse } from '~/api/listings/types'
import { useBusinessFeaturesStore } from '~/scopes/config/stores/businessFeaturesStore'

import { useFiltersOptions } from '../../../../composables/useFiltersOptions'
import translations from '../../Filters.translations'
import type { InitialFilterValues } from '../../Filters.types'

const { quickFiltersValues } = useFiltersOptions()

const filtersValues = defineModel<InitialFilterValues>('filtersValues', {
  required: true,
})

const props = defineProps<{
  submitFilters: () => void
}>()

const tracking = useTracking()
const { isFeatureEnabled } = useBusinessFeaturesStore()
const isDealIterationEnabled = isFeatureEnabled('isDealsListingsPageEnabled')
const i18n = useI18n()

const handleSeeAllWithinBackboxPriceRange = (filter: {
  maxRange: GetListingsFiltersMetadataResponse['backboxPriceDifferenceFastFilter'][number]['maxRange']
  minRange: GetListingsFiltersMetadataResponse['backboxPriceDifferenceFastFilter'][number]['minRange']
  rangeType: GetListingsFiltersMetadataResponse['backboxPriceDifferenceFastFilter'][number]['rangeType']
  currencyCode: GetListingsFiltersMetadataResponse['backboxPriceDifferenceFastFilter'][number]['currencyCode']
}) => {
  const isUsingUpperRange = quickFiltersValues.value?.every(
    (filterOption) => filterOption.maxRange <= filter.maxRange,
  )

  if (isUsingUpperRange) {
    tracking.trackClick({
      page_type: 'listings',
      zone: 'filter_shortcut',
      name: 'backboxes within higher range',
    })
  } else {
    tracking.trackClick({
      page_type: 'listings',
      zone: 'filter_shortcut',
      name: 'backboxes within lower range',
    })
  }

  filtersValues.value.backboxPriceMinRange = filter.minRange.toString()
  filtersValues.value.backboxPriceMaxRange = filter.maxRange.toString()
  filtersValues.value.backboxPriceRangeType = filter.rangeType
  filtersValues.value.backboxPriceCurrencyCode = filter.currencyCode
  filtersValues.value.hasBackbox = 'false'

  props.submitFilters()
}

const handleSeeAllDealOpportunities = () => {
  filtersValues.value.pricingStrategy = 'DEAL'
  props.submitFilters()
}
</script>
